import { endOfDay, max, min } from 'date-fns';

import type { DateRange } from './atoms';
import { getSessionDateGroupRange, type SessionDateGroup } from './date-group';
import type { UseQuerySessionsListParams } from './useQuerySessionsList';

/** Calendar groups plus Start Now's extra later-today cursor. */
export type GroupedSessionsQueryKey = SessionDateGroup | 'laterToday';

/** Group order on descending date sort (the default): future-to-oldest. */
export const SESSION_DATE_GROUP_ORDER: readonly SessionDateGroup[] = [
  'upcoming',
  'today',
  'yesterday',
  'older',
];

/** Ascending mirror: oldest-to-future, so the list stays one monotonic timeline. */
const SESSION_DATE_GROUP_ORDER_ASC: readonly SessionDateGroup[] = [
  ...SESSION_DATE_GROUP_ORDER,
].reverse();

/**
 * Group render order for a sort direction. Descending reads future-to-oldest;
 * ascending reverses it so the timeline stays monotonic across groups in the
 * direction the user chose, not just within each group.
 */
export function getSessionGroupRenderOrder(
  sortOrder: UseQuerySessionsListParams['sortOrder']
): readonly SessionDateGroup[] {
  return sortOrder === 'asc' ? SESSION_DATE_GROUP_ORDER_ASC : SESSION_DATE_GROUP_ORDER;
}

/**
 * Overlap of two date ranges (each side open when its bound is absent): the
 * later `from` and the earlier `to`. Lets a user's date-range filter narrow a
 * group's bounds — a filter outside a group yields an empty (`from > to`) range,
 * so that group returns nothing and its header drops.
 */
export function intersectDateRanges(a: DateRange, b: DateRange): DateRange {
  const froms = [a.from, b.from].filter((d): d is Date => d != null);
  const tos = [a.to, b.to].filter((d): d is Date => d != null);
  return {
    from: froms.length ? max(froms) : undefined,
    to: tos.length ? min(tos) : undefined,
  };
}

export type BuildSessionGroupParamsOptions = {
  /**
   * Collapsed-Upcoming mode (DESC date sort with Upcoming folded by default).
   * Under it the Upcoming group fetches ASC so page 1 is nearest-future first
   * (upward pagination extends away from Today). Query key derives from
   * params, so OFF→ON never serves mis-ordered cache pages.
   */
  collapsedUpcomingMode?: boolean;
  /**
   * Start Now's split-view Upcoming preview. It shares the nearest-first ASC
   * request shape with collapsed-Upcoming, but keeps its own presentation and
   * pagination semantics in `useSessionListData`.
   */
  startNowUpcomingMode?: boolean;
};

/**
 * Whether collapsed-Upcoming mode is actually in effect: requested AND the list
 * is DESC. Every behaviour the mode switches derives from this one predicate —
 * the ASC fetch flip ({@link buildSessionGroupParams}), and the display
 * reversal, group-gate exemption and bottom-pagination exclusion in
 * `useSessionListData`. They must agree: an ASC list renders Upcoming last, so
 * exempting it from bottom pagination there would strand the bottom-most group
 * with no trigger able to advance it.
 */
export function isCollapsedUpcomingActive(
  collapsedUpcomingMode: boolean,
  sortOrder: UseQuerySessionsListParams['sortOrder']
): boolean {
  return collapsedUpcomingMode && sortOrder === 'desc';
}

/**
 * Whether Start Now's Upcoming preview is in effect: requested AND the list is
 * DESC. DESC-only for the same reason as {@link isCollapsedUpcomingActive} — an
 * ASC list renders Upcoming last, where a preview above Today has no place.
 */
export function isStartNowUpcomingActive(
  startNowUpcomingMode: boolean,
  sortOrder: UseQuerySessionsListParams['sortOrder']
): boolean {
  return startNowUpcomingMode && sortOrder === 'desc';
}

/**
 * Derives a group's own query params from the shared base: each group is a
 * separate date-bounded request. Every calendar group keeps the list's own sort
 * direction (`base.sortOrder`) so ordering is continuous across groups; only
 * the date bounds differ — unless {@link isCollapsedUpcomingActive} or
 * {@link isStartNowUpcomingActive} flips Upcoming to ASC under a DESC list.
 * `laterToday` is Start Now's extra cursor
 * (now through end of day, always ASC) and is not a calendar header group.
 * `sortBy` is forced to `createdAt` (grouping is a date-sort-only treatment);
 * the bounds are intersected with any active date-range filter, and all other
 * filters pass through unchanged.
 */
export function buildSessionGroupParams(
  base: UseQuerySessionsListParams,
  group: GroupedSessionsQueryKey,
  now: Date,
  options?: BuildSessionGroupParamsOptions
): UseQuerySessionsListParams {
  const isLaterToday = group === 'laterToday';
  const groupRange = isLaterToday
    ? { from: now, to: endOfDay(now) }
    : getSessionDateGroupRange(group, now);
  const flipUpcoming =
    group === 'upcoming' &&
    (isCollapsedUpcomingActive(Boolean(options?.collapsedUpcomingMode), base.sortOrder) ||
      isStartNowUpcomingActive(Boolean(options?.startNowUpcomingMode), base.sortOrder));
  return {
    ...base,
    selectedDateRange: intersectDateRanges(groupRange, base.selectedDateRange),
    sortBy: 'createdAt',
    ...(isLaterToday || flipUpcoming ? { sortOrder: 'asc' as const } : null),
  };
}
